import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';

import { translate } from '@jsverse/transloco';

import { v4 as UUIDv4 } from 'uuid';

import { EnableClassTelemetry } from 'app/services/otel.service';
import { AlertService } from './alert.service';

export enum LocalStorageKey {
  PROJECT_PANEL_OPEN = 'PROJECT_PANEL_OPEN',
  WIDGET_GROUP_PANEL_OPEN = 'WIDGET_GROUP_PANEL_OPEN',
  ACTION_PLANS_PANEL_OPEN = 'ACTION_PLANS_PANEL_OPEN',
  CONFIGURATION_ITEM_LIST_OPTIONS = 'CONFIGURATION_ITEM_LIST_OPTIONS',
  LOCAL_USER_DOCUMENT_SEARCHES = 'LOCAL_USER_DOCUMENT_SEARCHES',
  SESSION_USER_DOCUMENT_SEARCHES = 'SESSION_USER_DOCUMENT_SEARCHES',
}

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  readonly LS_MAX_SIZE_IN_BYTES = 5 * 1024 * 1024;
  private alertService = inject(AlertService);

  readLsSignal = signal<string | boolean>(false);

  storageSizeInBytes = linkedSignal({
    source: this.readLsSignal,
    computation: () => this.getUsageInBytes(),
  });

  storageWarning = linkedSignal({
    source: this.storageSizeInBytes,
    computation: (bytes) => {
      return (bytes / this.LS_MAX_SIZE_IN_BYTES) * 100 >= 90;
    },
  });

  percentUsed = computed(() =>
    ((this.storageSizeInBytes() / this.LS_MAX_SIZE_IN_BYTES) * 100).toFixed(2)
  );

  get triggerActionId(): string {
    return UUIDv4();
  }

  /**
   * Retrieves an item from local or session storage.
   * @param key The key to retrieve from storage.
   * @param type The type of storage to retrieve from. Defaults to 'local'.
   * @returns The item (already parsed) retrieved from storage, or null if it does not exist.
   */
  getItem(key: LocalStorageKey | string, type: 'local' | 'session' = 'local') {
    const item = type === 'local' ? localStorage.getItem(key) : sessionStorage.getItem(key);
    if (!item || item === 'undefined') {
      return null;
    }
    const tryParse = (parasble) => {
      try {
        return JSON.parse(parasble);
      } catch (error) {
        return parasble;
      }
    };
    return tryParse(item);
  }

  /**
   * Writes an item to local or session storage.
   * If the item already exists, it will be overwritten.
   * @param key The key to write to storage.
   * @param value The (un-stringified) value to write to storage. It will be stringified later in another function.
   * @param type The type of storage to write to. Defaults to 'local'.
   * @param replacer A function that takes the key and value and returns a transformed value that will be written to storage.
   */
  setItem(
    key: LocalStorageKey | string,
    value: any,
    type: 'local' | 'session' = 'local',
    force?: boolean,
    replacer?: (key: string, value: any) => any
  ): void {
    type === 'local'
      ? this.writeItemToStorage(key, value, force, replacer)
      : sessionStorage.setItem(key, JSON.stringify(value));
    this.readLsSignal.set(`${key}_${this.triggerActionId}`);
  }

  private writeItemToStorage(
    key: LocalStorageKey | string,
    value: any,
    force = false,
    replacer?: (key: string, value: any) => any
  ) {
    if ((this.storageSizeInBytes() / this.LS_MAX_SIZE_IN_BYTES) * 100 >= 99) {
      this.alertService.showErrorToast(translate('storage-exceeded-quota-error'));
      return;
    }
    if (force) {
      localStorage.setItem(key, JSON.stringify(value, replacer));
    } else {
      try {
        localStorage.setItem(key, JSON.stringify(value, replacer));
      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          this.alertService.showErrorToast(
            `${translate('storage-exceeded-quota-error')}: ${error.message}`
          );
        }
      }
    }
  }

  removeItem(key: string, type: 'local' | 'session' = 'local'): void {
    type === 'local' ? localStorage.removeItem(key) : sessionStorage.removeItem(key);
    this.readLsSignal.set(`${key}_${this.triggerActionId}`);
  }

  clear(type: 'local' | 'session' = 'local') {
    type === 'local' ? localStorage.clear() : sessionStorage.clear();
    this.readLsSignal.set(undefined);
  }

  getUsageInBytes() {
    let totalSize = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        const keySize = new Blob([key]).size; // we need keysize since they take up space as well
        const valueSize = new Blob([localStorage[key]]).size;
        totalSize += keySize + valueSize;
      }
    }
    return totalSize;
  }
}
